import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { classificationByLabels } from "../../../lib/enums/ThreatStatusEnum";
import api from "../api";
import { PreviewTypes } from "../components/PreviewSample";
import { sampleReportKeys } from "../queryKeys";

const usePreviewSampleData = (
  sha1: string,
  previewType: string,
  searchAddress?: string,
  threatStatus?: number,
  beautify?: boolean,
  showPotentiallyMaliciousFile?: boolean
) => {
  const isHexQueryEnabled = previewType === PreviewTypes.HEX;
  const isTextQueryEnabled = previewType === PreviewTypes.TEXT;
  const isImageQueryEnabled =
    (previewType === PreviewTypes.IMAGE &&
      threatStatus === classificationByLabels.GOODWARE) ||
    showPotentiallyMaliciousFile;

  const hexPreviewDataQuery = useInfiniteQuery({
    queryKey: sampleReportKeys.getHexPreviewData(
      sha1,
      searchAddress,
      previewType,
      beautify
    ),
    queryFn: ({ pageParam: startAddress = 0 }) =>
      api.getHexPreviewData(sha1, searchAddress, startAddress),
    getNextPageParam: lastPage => lastPage.data.end_address,
    enabled: isHexQueryEnabled,
    initialPageParam: 0
  });

  const textPreviewDataQuery = useInfiniteQuery({
    queryKey: sampleReportKeys.getTextPreviewData(sha1, previewType, beautify),
    queryFn: ({ pageParam: startAddress = 0 }) =>
      api.getTextPreviewData(sha1, startAddress, beautify),
    getNextPageParam: lastPage => lastPage.data.end_address,
    enabled: isTextQueryEnabled,
    initialPageParam: 0
  });

  const imagePreviewThumbnailQuery = useQuery({
    queryKey: sampleReportKeys.getImagePreviewThumbnail(sha1),
    queryFn: () => api.getImagePreviewThumbnail(sha1),
    enabled: isImageQueryEnabled
  });

  const loadMore = () => {
    if (isTextQueryEnabled) {
      textPreviewDataQuery.fetchNextPage();
    } else {
      hexPreviewDataQuery.fetchNextPage();
    }
  };

  const checkHasMoreData = () => {
    const requestStatus = isTextQueryEnabled
      ? textPreviewDataQuery.data?.pages?.at(-1)?.status
      : hexPreviewDataQuery.data?.pages?.at(-1)?.status;

    return requestStatus === 206;
  };

  const hexPreviewDataLastPage = hexPreviewDataQuery.data?.pages?.at(-1);
  const textPreviewDataLastPage = textPreviewDataQuery.data?.pages?.at(-1);

  return {
    hexPreviewData: hexPreviewDataLastPage
      ? {
          ...hexPreviewDataLastPage?.data,
          content: hexPreviewDataQuery.data?.pages
            ?.map(({ data: { content } }) => content)
            .join("")
        }
      : undefined,
    textPreviewData: textPreviewDataLastPage
      ? {
          ...textPreviewDataLastPage?.data,
          content: textPreviewDataQuery.data?.pages
            ?.map(({ data: { content } }) => content)
            .join("")
        }
      : undefined,
    imagePreviewThumbnail: imagePreviewThumbnailQuery.data,
    hasMoreData: checkHasMoreData(),
    loadMore,
    error:
      (isHexQueryEnabled && hexPreviewDataQuery.error) ||
      (isTextQueryEnabled && textPreviewDataQuery.error) ||
      (isImageQueryEnabled && imagePreviewThumbnailQuery.error),
    isLoading:
      (isHexQueryEnabled &&
        (hexPreviewDataQuery.isLoading ||
          hexPreviewDataQuery.isFetchingNextPage)) ||
      (isTextQueryEnabled &&
        (textPreviewDataQuery.isLoading ||
          textPreviewDataQuery.isFetchingNextPage)) ||
      (isImageQueryEnabled && imagePreviewThumbnailQuery.isPending)
  };
};

export default usePreviewSampleData;
